// File Metadata:
// Filename: ThemeContext.jsx
// Version: 1.00
// Created: 2025-01-20
// Last Modified: 2025-01-20
// Authors: Dieuwer Descheemecker, GitHub Copilot
// Description: Theme context for managing light/dark mode throughout the application

import React, { createContext, useContext, useState, useEffect } from 'react';
import { ThemeProvider } from '@mui/material/styles';
import { CssBaseline, useMediaQuery } from '@mui/material';
import { createAppTheme } from '../styles/theme';

const ThemeContext = createContext();

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within a ThemeContextProvider');
  }
  return context;
};

export const ThemeContextProvider = ({ children }) => {
  // Get initial theme from localStorage or default to 'system'
  const [mode, setMode] = useState(() => {
    const savedMode = localStorage.getItem('familysync-theme-mode');
    return savedMode || 'system';
  });

  // Detect system theme preference
  const prefersDarkMode = useMediaQuery('(prefers-color-scheme: dark)');
  
  // Calculate effective theme mode
  const effectiveMode = mode === 'system' 
    ? (prefersDarkMode ? 'dark' : 'light')
    : mode;

  // Create theme based on effective mode
  const theme = createAppTheme(effectiveMode);

  // Save theme mode to localStorage whenever it changes
  useEffect(() => {
    localStorage.setItem('familysync-theme-mode', mode);
  }, [mode]);

  const toggleTheme = () => {
    setMode(prevMode => {
      switch (prevMode) {
        case 'light': return 'dark';
        case 'dark': return 'system';
        case 'system': return 'light';
        default: return 'light';
      }
    });
  };

  const setThemeMode = (newMode) => {
    if (['light', 'dark', 'system'].includes(newMode)) {
      setMode(newMode);
    }
  };

  const value = {
    mode,
    effectiveMode,
    theme,
    toggleTheme,
    setThemeMode,
    isDarkMode: effectiveMode === 'dark',
    isLightMode: effectiveMode === 'light',
    isSystemMode: mode === 'system',
  };

  return (
    <ThemeContext.Provider value={value}>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </ThemeProvider>
    </ThemeContext.Provider>
  );
};

export default ThemeContext;
